'use client';

import animations from '../animations.module.scss';
import styles from './ProcessSection.module.scss';
import { gaClick } from '@/src/helpers/gtag';
import { useInView } from '@/src/lib/hooks/useInView';

interface ProcessStep {
  number: string;
  title: string;
  description: string;
}

interface ProcessSectionProps {
  label: string;
  steps: ProcessStep[];
}

export function ProcessSection({ label, steps }: ProcessSectionProps) {
  const [ref, isInView] = useInView<HTMLElement>({ threshold: 0.1 });

  return (
    <section ref={ref} className={styles.section} aria-labelledby="process-label">
      <div className={styles.container}>
        <p
          id="process-label"
          className={`${styles.sectionLabel} ${animations.animateFadeInUp}`}
          style={{ animationPlayState: isInView ? 'running' : 'paused' }}
        >
          {label}
        </p>
        <div className={styles.timeline}>
          {/* Curved connector line for desktop */}
          <svg
            className={styles.connector}
            preserveAspectRatio="none"
            viewBox="0 0 1000 60"
            aria-hidden="true"
          >
            <path
              d="M0,30 Q125,0 250,30 T500,30 T750,30 T1000,30"
              className={styles.connectorPath}
            />
          </svg>

          {steps.map((step, index) => (
            <div
              key={step.number}
              className={`${styles.step} ${animations.animateFadeInUp}`}
              style={{
                animationDelay: `${0.2 * (index + 1)}s`,
                animationPlayState: isInView ? 'running' : 'paused',
              }}
              role="button"
              tabIndex={0}
              onClick={() => gaClick('process_section_step', { step_number: step.number })}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  gaClick('process_section_step', { step_number: step.number });
                }
              }}
            >
              <div className={styles.stepNumber}>{step.number}</div>
              <div className={styles.stepContent}>
                <h3 className={styles.stepTitle}>{step.title}</h3>
                <p className={styles.stepDescription}>{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
